import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CTASection from "../components/CTASection";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, ArrowRight, Calendar, Clock, User, Phone } from "lucide-react";
import { getPostBySlug, blogPosts } from "../data/blogPosts";
import { businessInfo } from "../data/mock";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) document.title = `${post.title} | Hamilton's Smog Blog`;
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <article>
          {/* Hero */}
          <section className="bg-slate-950 text-white pt-14 pb-16">
            <div className="max-w-4xl mx-auto px-4">
              <Link to="/blog" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6">
                <ArrowLeft className="w-4 h-4" /> All articles
              </Link>
              <div className="flex gap-2 flex-wrap mb-4">
                {post.tags.map((t) => (
                  <Badge key={t} className="bg-green-600 hover:bg-green-600 text-white border-0">{t}</Badge>
                ))}
              </div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">{post.title}</h1>
              <div className="flex items-center gap-5 text-sm text-slate-400 mt-6">
                <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.date}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readTime}</span>
              </div>
            </div>
          </section>

          {/* Cover */}
          <div className="max-w-4xl mx-auto px-4 -mt-8 mb-12 relative z-10">
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white aspect-[16/8] bg-slate-100">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Body */}
          <section className="pb-16">
            <div className="max-w-3xl mx-auto px-4">
              <div className="prose prose-slate max-w-none">
                {post.content.map((block, i) => {
                  if (block.type === "h2") {
                    return <h2 key={i} className="text-2xl md:text-3xl font-black text-slate-900 mt-10 mb-4 tracking-tight">{block.text}</h2>;
                  }
                  if (block.type === "p") {
                    return <p key={i} className="text-slate-700 leading-relaxed text-lg mb-4">{block.text}</p>;
                  }
                  if (block.type === "ul") {
                    return (
                      <ul key={i} className="list-disc pl-6 space-y-2 my-4 text-slate-700 text-lg">
                        {block.items.map((it, k) => <li key={k} className="leading-relaxed">{it}</li>)}
                      </ul>
                    );
                  }
                  return null;
                })}
              </div>

              {/* In-article CTA */}
              <Card className="mt-12 bg-slate-950 text-white border-0">
                <CardContent className="p-8 flex flex-col md:flex-row items-center gap-6 justify-between">
                  <div>
                    <h3 className="text-xl md:text-2xl font-black">Need a smog check today?</h3>
                    <p className="text-slate-300 mt-1">STAR-certified. 20-minute turnaround. Novato, CA.</p>
                  </div>
                  <div className="flex gap-3">
                    <Button asChild className="bg-green-600 hover:bg-green-700 font-bold">
                      <a href={businessInfo.bookingUrl} target="_blank" rel="noreferrer">Book Now</a>
                    </Button>
                    <Button asChild variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-slate-900 font-bold">
                      <a href={`tel:${businessInfo.phoneRaw}`}><Phone className="w-4 h-4 mr-2" /> Call</a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Related */}
          <section className="py-16 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-8 tracking-tight">Keep reading</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((p) => (
                  <Link to={`/blog/${p.slug}`} key={p.slug} className="group">
                    <Card className="card-hover border-slate-200 h-full overflow-hidden">
                      <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-black text-slate-900 group-hover:text-green-600 transition-colors leading-snug">{p.title}</h3>
                        <div className="inline-flex items-center gap-1.5 text-green-600 font-bold text-sm mt-3">
                          Read more <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </article>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
